import { useMemo, useState } from "react";
import type { Row } from "../types";
import {
  BotaoRemover,
  Progresso,
  SecaoHead,
  Vazio,
  dataBr,
  moeda,
  paraNumero,
  tomDaPrioridade,
} from "./EventoPecas";
import { IconCheck, IconPlus, IconSearch, IconLink } from "../icons";
import { ordenarPorNome } from "../ordem";

/**
 * As abas de LISTA da central do evento.
 *
 * Todas seguem o mesmo contrato: recebem a lista e devolvem a lista alterada
 * (`onChange`). Quem salva é a central — assim a pessoa mexe em várias linhas e
 * grava uma vez só, como já acontece no plano de ação do RH e no roteiro de
 * entrevista. Nenhuma delas fala com a API.
 */

export interface Atividade {
  id?: number;
  dia: string;
  hora_inicio: string;
  hora_fim: string;
  titulo: string;
  descricao: string;
  responsavel: string;
  local: string;
  observacoes: string;
  ordem?: number;
}

const ATIVIDADE_VAZIA: Atividade = {
  dia: "",
  hora_inicio: "",
  hora_fim: "",
  titulo: "",
  descricao: "",
  responsavel: "",
  local: "",
  observacoes: "",
};

/* ================== PROGRAMAÇÃO (timeline) ================== */

export function AbaProgramacao({
  itens,
  onChange,
  diaPadrao,
}: {
  itens: Atividade[];
  onChange: (l: Atividade[]) => void;
  diaPadrao?: string | null;
}) {
  const [editando, setEditando] = useState<number | null>(null);
  /** Qual atividade está sendo arrastada (índice na lista). */
  const [arrastando, setArrastando] = useState<number | null>(null);
  const [alvo, setAlvo] = useState<number | null>(null);

  /**
   * Reordenar arrastando, com o recurso do próprio navegador (HTML5) — sem
   * biblioteca. As setas continuam ali: arrastar não funciona bem em tela de
   * toque, e o celular é onde metade das pessoas abre o sistema.
   */
  function soltarEm(destino: number) {
    const origem = arrastando;
    setArrastando(null);
    setAlvo(null);
    if (origem === null || origem === destino) return;
    const copia = [...itens];
    const [movido] = copia.splice(origem, 1);
    copia.splice(destino, 0, movido!);
    onChange(copia.map((a, x) => ({ ...a, ordem: x })));
  }

  /** Agrupa por dia — um evento de três dias tem três blocos de timeline. */
  const porDia = useMemo(() => {
    const mapa = new Map<string, { item: Atividade; i: number }[]>();
    itens.forEach((item, i) => {
      const chave = item.dia || "";
      if (!mapa.has(chave)) mapa.set(chave, []);
      mapa.get(chave)!.push({ item, i });
    });
    return [...mapa.entries()].sort((a, b) => a[0].localeCompare(b[0]));
  }, [itens]);

  function mudar(i: number, campo: keyof Atividade, valor: string) {
    onChange(itens.map((a, x) => (x === i ? { ...a, [campo]: valor } : a)));
  }

  /** Sobe/desce a atividade — a ordem manda quando duas têm o mesmo horário. */
  function mover(i: number, direcao: -1 | 1) {
    const destino = i + direcao;
    if (destino < 0 || destino >= itens.length) return;
    const copia = [...itens];
    [copia[i], copia[destino]] = [copia[destino]!, copia[i]!];
    onChange(copia.map((a, x) => ({ ...a, ordem: x })));
  }

  function duplicar(i: number) {
    const copia = [...itens];
    copia.splice(i + 1, 0, { ...itens[i]!, id: undefined, titulo: `${itens[i]!.titulo} (cópia)` });
    onChange(copia.map((a, x) => ({ ...a, ordem: x })));
  }

  return (
    <div className="ev-aba">
      <SecaoHead
        titulo="Programação"
        contagem={`${itens.length} atividade(s)`}
        acao="Adicionar atividade"
        onAcao={() => {
          onChange([...itens, { ...ATIVIDADE_VAZIA, dia: diaPadrao ?? "", ordem: itens.length }]);
          setEditando(itens.length);
        }}
      />

      {itens.length === 0 ? (
        <Vazio
          texto="Nenhuma atividade na programação"
          dica="Monte o roteiro do dia: abertura, palestra, almoço, encerramento…"
        />
      ) : (
        porDia.map(([dia, linhas]) => (
          <section key={dia || "sem-dia"} className="ev-timeline-dia">
            <h5 className="ev-timeline-data">{dia ? dataBr(dia) : "Sem dia definido"}</h5>
            <ol className="ev-timeline">
              {linhas.map(({ item, i }) => (
                <li
                  key={item.id ?? `novo-${i}`}
                  className={`ev-timeline-item ${arrastando === i ? "arrastando" : ""} ${
                    alvo === i && arrastando !== null && arrastando !== i ? "alvo" : ""
                  }`}
                  // Só arrasta pela alça (☰): sem isso, selecionar o texto de
                  // uma atividade viraria arrastar sem querer.
                  draggable={arrastando === i}
                  onDragStart={(e) => {
                    e.dataTransfer.effectAllowed = "move";
                    // Firefox só inicia o arraste com algum dado definido.
                    e.dataTransfer.setData("text/plain", String(i));
                  }}
                  onDragOver={(e) => {
                    e.preventDefault();
                    e.dataTransfer.dropEffect = "move";
                    if (alvo !== i) setAlvo(i);
                  }}
                  onDrop={(e) => {
                    e.preventDefault();
                    soltarEm(i);
                  }}
                  onDragEnd={() => {
                    setArrastando(null);
                    setAlvo(null);
                  }}
                >
                  <div className="ev-timeline-hora">
                    <strong>{item.hora_inicio || "--:--"}</strong>
                    {item.hora_fim && <small>{item.hora_fim}</small>}
                  </div>
                  <span className="ev-timeline-marca" aria-hidden="true" />
                  <div className="ev-timeline-corpo">
                    {editando === i ? (
                      <div className="ev-form-linha">
                        <label className="field">
                          <span>Dia</span>
                          <input type="date" value={item.dia} onChange={(e) => mudar(i, "dia", e.target.value)} />
                        </label>
                        <label className="field">
                          <span>Início</span>
                          <input type="time" value={item.hora_inicio} onChange={(e) => mudar(i, "hora_inicio", e.target.value)} />
                        </label>
                        <label className="field">
                          <span>Término</span>
                          <input type="time" value={item.hora_fim} onChange={(e) => mudar(i, "hora_fim", e.target.value)} />
                        </label>
                        <label className="field field-wide">
                          <span>Atividade <em className="req">*</em></span>
                          <input value={item.titulo} onChange={(e) => mudar(i, "titulo", e.target.value)} placeholder="Ex.: Abertura" autoFocus />
                        </label>
                        <label className="field">
                          <span>Responsável</span>
                          <input value={item.responsavel} onChange={(e) => mudar(i, "responsavel", e.target.value)} />
                        </label>
                        <label className="field">
                          <span>Local</span>
                          <input value={item.local} onChange={(e) => mudar(i, "local", e.target.value)} placeholder="Ex.: Auditório" />
                        </label>
                        <label className="field field-wide">
                          <span>Descrição</span>
                          <input value={item.descricao} onChange={(e) => mudar(i, "descricao", e.target.value)} />
                        </label>
                        <div className="ev-form-acoes">
                          <button type="button" className="btn-secondary btn-sm" onClick={() => setEditando(null)}>
                            Pronto
                          </button>
                        </div>
                      </div>
                    ) : (
                      <button type="button" className="ev-timeline-texto" onClick={() => setEditando(i)}>
                        <strong>{item.titulo || "(sem título)"}</strong>
                        {item.descricao && <small>{item.descricao}</small>}
                        <small className="muted">
                          {[item.responsavel, item.local].filter(Boolean).join(" · ")}
                        </small>
                      </button>
                    )}
                  </div>
                  <div className="ev-timeline-acoes">
                    <span
                      className="ev-arrastar"
                      title="Arraste para reordenar"
                      // O arraste é habilitado ao segurar a alça e desligado ao
                      // soltar — é o que impede o arraste acidental do texto.
                      onMouseDown={() => setArrastando(i)}
                      onMouseUp={() => setArrastando(null)}
                    >
                      ☰
                    </span>
                    <button type="button" className="icon-btn" title="Subir" onClick={() => mover(i, -1)}>↑</button>
                    <button type="button" className="icon-btn" title="Descer" onClick={() => mover(i, 1)}>↓</button>
                    <button type="button" className="icon-btn" title="Duplicar" onClick={() => duplicar(i)}>⧉</button>
                    <BotaoRemover
                      titulo="Tirar da programação"
                      onClick={() => onChange(itens.filter((_, x) => x !== i))}
                    />
                  </div>
                </li>
              ))}
            </ol>
          </section>
        ))
      )}
    </div>
  );
}

/* ================== CHECKLIST ================== */

export interface ItemChecklist {
  id?: number;
  titulo: string;
  responsavel: string;
  prazo: string;
  prioridade: string;
  concluido: boolean;
  concluido_em?: string | null;
  concluido_por?: string | null;
}

const PRIORIDADES = ["Baixa", "Média", "Alta", "Urgente"];

export function AbaChecklist({
  itens,
  onChange,
  usuario,
}: {
  itens: ItemChecklist[];
  onChange: (l: ItemChecklist[]) => void;
  usuario?: string;
}) {
  const feitas = itens.filter((i) => i.concluido).length;
  const pct = itens.length ? Math.round((feitas / itens.length) * 100) : 0;

  function mudar(i: number, campo: keyof ItemChecklist, valor: string | boolean) {
    onChange(
      itens.map((t, x) =>
        x === i
          ? {
              ...t,
              [campo]: valor,
              // Marcar como feito guarda QUEM e QUANDO — é o que permite
              // cobrar depois sem depender da memória de alguém.
              ...(campo === "concluido" && valor === true
                ? { concluido_em: new Date().toISOString(), concluido_por: usuario ?? "" }
                : {}),
              ...(campo === "concluido" && valor === false
                ? { concluido_em: null, concluido_por: null }
                : {}),
            }
          : t,
      ),
    );
  }

  return (
    <div className="ev-aba">
      <SecaoHead
        titulo="Checklist"
        contagem={`${feitas} de ${itens.length} concluída(s)`}
        acao="Adicionar tarefa"
        onAcao={() =>
          onChange([
            ...itens,
            { titulo: "", responsavel: "", prazo: "", prioridade: "Média", concluido: false },
          ])
        }
      />

      {itens.length > 0 && (
        <div className="ev-progresso">
          <Progresso pct={pct} />
          <span className="muted">{pct}%</span>
        </div>
      )}

      {itens.length === 0 ? (
        <Vazio
          texto="Nenhuma tarefa no checklist"
          dica="Ex.: definir local, confirmar palestrante, criar arte, comprar materiais…"
        />
      ) : (
        <ul className="ev-check-lista">
          {itens.map((t, i) => (
            <li key={t.id ?? `novo-${i}`} className={`ev-check-item ${t.concluido ? "feito" : ""}`}>
              <label className="ev-check-marca">
                <input
                  type="checkbox"
                  checked={t.concluido}
                  onChange={(e) => mudar(i, "concluido", e.target.checked)}
                />
                {t.concluido && <IconCheck size={13} />}
              </label>
              <input
                className="ev-check-titulo"
                value={t.titulo}
                placeholder="O que precisa ser feito"
                onChange={(e) => mudar(i, "titulo", e.target.value)}
              />
              <input
                className="ev-check-resp"
                value={t.responsavel}
                placeholder="Responsável"
                onChange={(e) => mudar(i, "responsavel", e.target.value)}
              />
              <input
                type="date"
                className="ev-check-prazo"
                value={t.prazo}
                onChange={(e) => mudar(i, "prazo", e.target.value)}
              />
              <select
                className={`ev-check-prio pill ${tomDaPrioridade(t.prioridade)}`}
                value={t.prioridade}
                onChange={(e) => mudar(i, "prioridade", e.target.value)}
              >
                {PRIORIDADES.map((p) => (
                  <option key={p} value={p}>
                    {p}
                  </option>
                ))}
              </select>
              <BotaoRemover
                titulo="Tirar do checklist"
                onClick={() => onChange(itens.filter((_, x) => x !== i))}
              />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/* ================== EQUIPE ================== */

export interface Membro {
  id?: number;
  colaborador_id?: number | null;
  nome: string;
  funcao: string;
  contato: string;
  observacao: string;
}

export function AbaEquipe({
  itens,
  onChange,
  colaboradores,
}: {
  itens: Membro[];
  onChange: (l: Membro[]) => void;
  colaboradores: Row[];
}) {
  /** Opções do select "Colaborador" em ordem alfabética (as linhas não mudam de lugar). */
  const colaboradoresAZ = useMemo(() => ordenarPorNome(colaboradores, (c) => c.nome), [colaboradores]);

  function mudar(i: number, campo: keyof Membro, valor: string) {
    onChange(itens.map((m, x) => (x === i ? { ...m, [campo]: valor } : m)));
  }

  /** Escolher alguém do cadastro preenche nome e contato (ninguém redigita). */
  function escolher(i: number, colaboradorId: string) {
    const c = colaboradores.find((x) => String(x.id) === colaboradorId);
    onChange(
      itens.map((m, x) =>
        x === i
          ? {
              ...m,
              colaborador_id: c ? Number(c.id) : null,
              nome: c ? String(c.nome) : m.nome,
              contato: c?.telefone ? String(c.telefone) : m.contato,
              funcao: m.funcao || (c?.cargo ? String(c.cargo) : ""),
            }
          : m,
      ),
    );
  }

  return (
    <div className="ev-aba">
      <SecaoHead
        titulo="Equipe do evento"
        contagem={`${itens.length} pessoa(s)`}
        acao="Adicionar pessoa"
        onAcao={() =>
          onChange([...itens, { colaborador_id: null, nome: "", funcao: "", contato: "", observacao: "" }])
        }
      />

      {itens.length === 0 ? (
        <Vazio texto="Ninguém na equipe ainda" dica="Quem organiza, apoia e responde pelo evento." />
      ) : (
        <div className="ev-lista-grade">
          {itens.map((m, i) => (
            <div className="ev-linha" key={m.id ?? `novo-${i}`}>
              <label className="field">
                <span>Colaborador</span>
                <select value={m.colaborador_id ?? ""} onChange={(e) => escolher(i, e.target.value)}>
                  <option value="">— de fora do cadastro —</option>
                  {colaboradoresAZ.map((c) => (
                    <option key={String(c.id)} value={String(c.id)}>
                      {String(c.nome)}
                    </option>
                  ))}
                </select>
              </label>
              <label className="field">
                <span>Nome <em className="req">*</em></span>
                <input value={m.nome} onChange={(e) => mudar(i, "nome", e.target.value)} />
              </label>
              <label className="field">
                <span>Função no evento</span>
                <input value={m.funcao} onChange={(e) => mudar(i, "funcao", e.target.value)} placeholder="Ex.: Logística" />
              </label>
              <label className="field">
                <span>Contato</span>
                <input value={m.contato} onChange={(e) => mudar(i, "contato", e.target.value)} />
              </label>
              <BotaoRemover titulo="Tirar da equipe" onClick={() => onChange(itens.filter((_, x) => x !== i))} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ================== PARTICIPANTES ================== */

export interface Participante {
  id?: number;
  colaborador_id?: number | null;
  nome: string;
  situacao: string;
  presente: boolean;
  observacao?: string;
}

const SITUACOES = ["Pendente", "Confirmado", "Recusado"];

export function AbaParticipantes({
  itens,
  onChange,
  colaboradores,
  onAdicionarEmLote,
}: {
  itens: Participante[];
  onChange: (l: Participante[]) => void;
  colaboradores: Row[];
  onAdicionarEmLote: (ids: number[]) => void;
}) {
  const [busca, setBusca] = useState("");
  const [escolhendo, setEscolhendo] = useState(false);
  const [marcados, setMarcados] = useState<Set<number>>(new Set());

  const jaNoEvento = new Set(itens.map((p) => p.colaborador_id).filter(Boolean) as number[]);
  // Lista de marcação em ordem alfabética (src/ordem.ts).
  const disponiveis = ordenarPorNome(
    colaboradores.filter((c) => !jaNoEvento.has(Number(c.id))),
    (c) => c.nome,
  );
  /**
   * Tabela de participantes em ordem alfabética SÓ NA TELA: cada linha leva o
   * índice original, porque as edições (situação, presença, remover) mexem na
   * lista pela posição.
   */
  const linhasAZ = ordenarPorNome(
    itens.map((p, i) => ({ p, i })),
    (x) => x.p.nome,
  );
  const filtrados = busca
    ? disponiveis.filter((c) => String(c.nome).toLowerCase().includes(busca.toLowerCase()))
    : disponiveis;

  const confirmados = itens.filter((p) => p.situacao === "Confirmado").length;
  const presentes = itens.filter((p) => p.presente).length;

  return (
    <div className="ev-aba">
      <SecaoHead
        titulo="Participantes"
        contagem={`${itens.length} · ${confirmados} confirmado(s) · ${presentes} presente(s)`}
        acao={escolhendo ? "Fechar" : "Adicionar colaboradores"}
        onAcao={() => setEscolhendo((v) => !v)}
      />

      {escolhendo && (
        <div className="ev-escolher">
          <div className="search-wrap">
            <IconSearch size={16} />
            <input
              className="search"
              placeholder="Buscar colaborador..."
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
            />
          </div>
          <div className="ev-escolher-lista">
            {filtrados.length === 0 ? (
              <span className="muted">Todos os colaboradores já estão no evento.</span>
            ) : (
              filtrados.map((c) => {
                const cid = Number(c.id);
                return (
                  <label key={cid} className={`ev-escolher-item ${marcados.has(cid) ? "marcado" : ""}`}>
                    <input
                      type="checkbox"
                      checked={marcados.has(cid)}
                      onChange={() => {
                        const novo = new Set(marcados);
                        novo.has(cid) ? novo.delete(cid) : novo.add(cid);
                        setMarcados(novo);
                      }}
                    />
                    <span>{String(c.nome)}</span>
                    <small className="muted">{String(c.departamento ?? c.cargo ?? "")}</small>
                  </label>
                );
              })
            )}
          </div>
          <div className="ev-escolher-acoes">
            <button
              type="button"
              className="btn-secondary btn-sm"
              onClick={() => setMarcados(new Set(filtrados.map((c) => Number(c.id))))}
            >
              Marcar os {filtrados.length} da lista
            </button>
            <button
              type="button"
              className="btn-primary btn-sm"
              disabled={marcados.size === 0}
              onClick={() => {
                onAdicionarEmLote([...marcados]);
                setMarcados(new Set());
                setEscolhendo(false);
              }}
            >
              <IconPlus size={15} /> Adicionar {marcados.size > 0 ? `(${marcados.size})` : ""}
            </button>
          </div>
        </div>
      )}

      {itens.length === 0 ? (
        <Vazio texto="Nenhum participante" dica="Adicione os colaboradores convidados para o evento." />
      ) : (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>Participante</th>
                <th>Situação</th>
                <th>Presença</th>
                <th className="col-actions">Ações</th>
              </tr>
            </thead>
            <tbody>
              {linhasAZ.map(({ p, i }) => (
                <tr key={p.id ?? `novo-${i}`}>
                  <td>{p.nome}</td>
                  <td>
                    <select
                      className="ev-select-linha"
                      value={p.situacao}
                      onChange={(e) =>
                        onChange(itens.map((x, y) => (y === i ? { ...x, situacao: e.target.value } : x)))
                      }
                    >
                      {SITUACOES.map((s) => (
                        <option key={s} value={s}>
                          {s}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td>
                    <label className="ev-presenca">
                      <input
                        type="checkbox"
                        checked={p.presente}
                        onChange={(e) =>
                          onChange(itens.map((x, y) => (y === i ? { ...x, presente: e.target.checked } : x)))
                        }
                      />
                      <span>{p.presente ? "Presente" : "—"}</span>
                    </label>
                  </td>
                  <td className="col-actions">
                    <BotaoRemover
                      titulo="Tirar do evento"
                      onClick={() => onChange(itens.filter((_, x) => x !== i))}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* ================== ORÇAMENTO ================== */

export interface Despesa {
  id?: number;
  descricao: string;
  categoria: string;
  previsto: string;
  realizado: string;
  status: string;
  observacao: string;
}

const STATUS_DESPESA = ["Previsto", "Contratado", "Pago", "Cancelado"];

export function AbaOrcamento({ itens, onChange }: { itens: Despesa[]; onChange: (l: Despesa[]) => void }) {
  const previsto = itens.reduce((s, d) => s + paraNumero(d.previsto), 0);
  const realizado = itens.reduce((s, d) => s + paraNumero(d.realizado), 0);
  const diferenca = previsto - realizado;

  function mudar(i: number, campo: keyof Despesa, valor: string) {
    onChange(itens.map((d, x) => (x === i ? { ...d, [campo]: valor } : d)));
  }

  return (
    <div className="ev-aba">
      <SecaoHead
        titulo="Orçamento"
        contagem={`${itens.length} item(ns)`}
        acao="Adicionar despesa"
        onAcao={() =>
          onChange([
            ...itens,
            { descricao: "", categoria: "", previsto: "", realizado: "", status: "Previsto", observacao: "" },
          ])
        }
      />

      {itens.length > 0 && (
        <div className="ev-orcamento-totais">
          <div>
            <span className="muted">Previsto</span>
            <strong>{moeda(previsto)}</strong>
          </div>
          <div>
            <span className="muted">Realizado</span>
            <strong>{moeda(realizado)}</strong>
          </div>
          <div className={diferenca < 0 ? "ev-estourou" : ""}>
            <span className="muted">Diferença</span>
            {/* Negativo = gastou mais que o previsto. A cor avisa antes da conta. */}
            <strong>{moeda(diferenca)}</strong>
          </div>
        </div>
      )}

      {itens.length === 0 ? (
        <Vazio texto="Sem despesas lançadas" dica="Ex.: coffee break, brindes, decoração, palestrante…" />
      ) : (
        <div className="ev-lista-grade">
          {itens.map((d, i) => (
            <div className="ev-linha" key={d.id ?? `novo-${i}`}>
              <label className="field field-wide">
                <span>Descrição <em className="req">*</em></span>
                <input value={d.descricao} onChange={(e) => mudar(i, "descricao", e.target.value)} />
              </label>
              <label className="field">
                <span>Categoria</span>
                <input value={d.categoria} onChange={(e) => mudar(i, "categoria", e.target.value)} />
              </label>
              <label className="field">
                <span>Previsto</span>
                <input value={d.previsto} onChange={(e) => mudar(i, "previsto", e.target.value)} placeholder="0,00" inputMode="decimal" />
              </label>
              <label className="field">
                <span>Realizado</span>
                <input value={d.realizado} onChange={(e) => mudar(i, "realizado", e.target.value)} placeholder="0,00" inputMode="decimal" />
              </label>
              <label className="field">
                <span>Status</span>
                <select value={d.status} onChange={(e) => mudar(i, "status", e.target.value)}>
                  {STATUS_DESPESA.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
              </label>
              <BotaoRemover titulo="Tirar do orçamento" onClick={() => onChange(itens.filter((_, x) => x !== i))} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ================== LINKS ================== */

export interface LinkEvento {
  id?: number;
  nome: string;
  url: string;
  descricao: string;
}

/* ================== ANEXOS ================== */

export interface Anexo {
  id: number;
  arquivo: string;
  nome: string;
  tamanho: number;
  enviado_em: string;
  enviado_por?: string | null;
  disponivel: boolean;
  url: string;
}

function tamanhoLegivel(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1048576).toFixed(1)} MB`;
}

/**
 * Os documentos do evento. Diferente do álbum: aqui entram cronograma,
 * briefing, lista de presença, planilha de custos e a arte para aprovação.
 * O envio é imediato (não espera o "Salvar evento") — arquivo é assim mesmo:
 * escolheu, subiu.
 */
export function AbaAnexos({
  itens,
  enviando,
  podeExcluir,
  onEnviar,
  onRemover,
}: {
  itens: Anexo[];
  enviando: boolean;
  podeExcluir: boolean;
  onEnviar: (files: FileList) => void;
  onRemover: (a: Anexo) => void;
}) {
  return (
    <div className="ev-aba">
      <SecaoHead titulo="Arquivos do evento" contagem={`${itens.length} arquivo(s)`}>
        <label className={`btn-secondary btn-sm ev-upload ${enviando ? "ocupado" : ""}`}>
          <IconPlus size={15} /> {enviando ? "Enviando…" : "Anexar arquivo"}
          <input
            type="file"
            multiple
            hidden
            disabled={enviando}
            accept=".pdf,.doc,.docx,.xls,.xlsx,.xlsm,.csv,.ppt,.pptx,.txt,.rtf,.odt,.ods,.zip,image/*"
            onChange={(e) => {
              if (e.target.files?.length) onEnviar(e.target.files);
              e.target.value = "";
            }}
          />
        </label>
      </SecaoHead>

      {itens.length === 0 ? (
        <Vazio
          texto="Nenhum arquivo anexado"
          dica="Cronograma, briefing, arte de divulgação, lista de participantes, orçamento…"
        />
      ) : (
        <ul className="ev-anexos">
          {itens.map((a) => (
            <li key={a.id}>
              <a href={a.url} target="_blank" rel="noreferrer" title="Abrir / baixar">
                📎 {a.nome}
              </a>
              <small className="muted">
                {tamanhoLegivel(a.tamanho)} ·{" "}
                {new Date(a.enviado_em).toLocaleDateString("pt-BR")}
                {a.enviado_por ? ` · ${a.enviado_por}` : ""}
                {!a.disponivel && " · arquivo não encontrado no servidor"}
              </small>
              {podeExcluir && <BotaoRemover titulo="Excluir arquivo" onClick={() => onRemover(a)} />}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export function AbaLinks({ itens, onChange }: { itens: LinkEvento[]; onChange: (l: LinkEvento[]) => void }) {
  function mudar(i: number, campo: keyof LinkEvento, valor: string) {
    onChange(itens.map((l, x) => (x === i ? { ...l, [campo]: valor } : l)));
  }

  return (
    <div className="ev-aba">
      <SecaoHead
        titulo="Links importantes"
        contagem={`${itens.length} link(s)`}
        acao="Adicionar link"
        onAcao={() => onChange([...itens, { nome: "", url: "", descricao: "" }])}
      />

      {itens.length === 0 ? (
        <Vazio texto="Nenhum link" dica="Mapa, formulário de inscrição, grupo do WhatsApp, pasta de materiais…" />
      ) : (
        <div className="ev-lista-grade">
          {itens.map((l, i) => (
            <div className="ev-linha" key={l.id ?? `novo-${i}`}>
              <label className="field">
                <span>Nome <em className="req">*</em></span>
                <input value={l.nome} onChange={(e) => mudar(i, "nome", e.target.value)} placeholder="Ex.: Inscrição" />
              </label>
              <label className="field field-wide">
                <span>Endereço <em className="req">*</em></span>
                <input value={l.url} onChange={(e) => mudar(i, "url", e.target.value)} placeholder="forms.gle/..." />
              </label>
              <label className="field">
                <span>Descrição</span>
                <input value={l.descricao} onChange={(e) => mudar(i, "descricao", e.target.value)} />
              </label>
              {l.url && (
                <a className="icon-btn" href={l.url.startsWith("http") ? l.url : `https://${l.url}`} target="_blank" rel="noreferrer" title="Abrir">
                  <IconLink size={15} />
                </a>
              )}
              <BotaoRemover titulo="Tirar o link" onClick={() => onChange(itens.filter((_, x) => x !== i))} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
